import { useCallback, useMemo } from 'react';

import InteractiveDragDisplay from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-display';
import {
  InteractiveResponseViewScaler,
  InteractiveResponseTitle,
} from '../../shared/interactive-response-shared';
import {
  RESPONSE_FREE,
  ViewProps,
} from '../interactive-response-categorise.constants';
import { InteractiveDropDisplay } from './interactive-response-categorise-drop-display';
import { InteractiveCategoriseDropItemTarget } from './interactive-response-categorise-drop.style';
import { initialise } from './interactive-response-categorise-play.utils';
import * as Styled from './interactive-response-categorise.style';
import { ComponentDimensions } from 'approot/activity-builder/components/shared/component-height/component-dimensions';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { defaultDimensions } from '../../interactive-response-shared.constants';
import { InteractiveResponsePreviewContainer } from '../../shared/interactive-response-shared.style';
import { getShouldShowNumberOfItemsHint } from '../settings/interactive-response-categorise.utils';
import { useBatchComponentUpdateContext } from 'approot/activity-builder/components/shared/batch-component-update.context';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';

const InteractiveResponseCategorisePlayPreview = ({
  id,
  properties,
  sectionId,
  instructionHeight,
  shouldShuffle,
}: ViewProps & {
  instructionHeight?: number;
  shouldShuffle: boolean;
}) => {
  const { activeComponent } = useActivityBuilderContext();
  const { updateComponentProperties } = useBatchComponentUpdateContext();
  const { contentType } = useGetInteractiveLessonParams();

  const isFreeResponse =
    properties.interactiveResponseCategorise.answerType === RESPONSE_FREE;
  const isShowNumberOfItemsHint = getShouldShowNumberOfItemsHint(
    properties.interactiveResponseCategorise.showNumberOfItemsHint,
    contentType
  );

  const { dragItems, dropItems } = useMemo(() => {
    return initialise({
      items: properties.interactiveResponseCategorise.items,
      categories: properties.interactiveResponseCategorise.categories,
      showAnswer: !isFreeResponse,
      isShowNumberOfItemsHint,
      shouldShuffle,
      variant: properties.interactiveResponseCategorise.variant,
    });
  }, [
    isFreeResponse,
    isShowNumberOfItemsHint,
    shouldShuffle,
    properties.interactiveResponseCategorise.items,
    properties.interactiveResponseCategorise.categories,
    properties.interactiveResponseCategorise.variant,
  ]);

  const handleHeightUpdate = useCallback(
    (width: number, height: number) => {
      const dimensionData = {
        __typename: 'ComponentDimension',
        width,
        height: Math.floor(height),
      };
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              dimensionData,
            },
          },
        },
        properties: {
          ...properties,
          // @ts-ignore categoriseDimensionData is an alias TS cant understand
          dimensionData: {
            ...dimensionData,
            __typename: 'ComponentDimension',
          },
        },
        typename: 'ActivitiesCategoriseResponseComponent',
      });
    },
    [id, properties, sectionId, updateComponentProperties]
  );

  return (
    <ComponentDimensions
      id={id}
      showHandle={activeComponent?.id === id}
      defaultValue={properties.categoriseDimensionData || defaultDimensions}
      onResizeEnd={handleHeightUpdate}
      extraHeight={instructionHeight}
    >
      <InteractiveResponseViewScaler
        key={properties.interactiveResponseCategorise.answerType}
      >
        <InteractiveResponseTitle
          title={properties.interactiveResponseCategorise.title}
          subTitle={properties.interactiveResponseCategorise.subTitle}
        />

        <InteractiveResponsePreviewContainer data-selector="interactive-response-preview-container">
          {dragItems.map((dragItem, index) => (
            <Styled.InteractiveResponseCategoryContainerItem
              key={dragItem.id}
              data-selector={`interactive-response-category-container-item-${index}`}
            >
              <InteractiveDragDisplay
                imageUrl={dragItem.question.image?.small}
                isFit={dragItem.question.image?.isFit}
                description={dragItem.question.image?.description || ''}
                baseColour={dragItem.question.baseColour}
                variant={dragItem.question.variant}
                text={dragItem.question.text}
                richtext={dragItem.question.richtext}
              />
            </Styled.InteractiveResponseCategoryContainerItem>
          ))}
        </InteractiveResponsePreviewContainer>

        <Styled.InteractiveResponseCategories data-selector="interactive-response-categories">
          {dropItems.map((dropItem, index) => {
            return (
              <Styled.InteractiveResponseCategory
                key={dropItem.id}
                data-selector={`interactive-response-category-${index}`}
              >
                <Styled.InteractiveResponseCategoryLabel>
                  {dropItem.label}
                </Styled.InteractiveResponseCategoryLabel>
                <InteractiveCategoriseDropItemTarget
                  data-selector={`interactive-categorise-drop-item-target-${index}`}
                >
                  {isShowNumberOfItemsHint && (
                    <>
                      {dropItem.zones.map((zone, index) => {
                        return (
                          <InteractiveDropDisplay
                            key={index}
                            hasDropped={false}
                            isContinuousMarking={false}
                          >
                            <div />
                          </InteractiveDropDisplay>
                        );
                      })}
                    </>
                  )}
                </InteractiveCategoriseDropItemTarget>
              </Styled.InteractiveResponseCategory>
            );
          })}
        </Styled.InteractiveResponseCategories>
      </InteractiveResponseViewScaler>
    </ComponentDimensions>
  );
};

export default InteractiveResponseCategorisePlayPreview;
